import {
    scheduleLaterToday,
    scheduleThisEvening,
    scheduleTomorrow,
    scheduleThisWeekend,
    scheduleNextWeek,
    scheduleChooseDate,
    unsnooze,
    scheduleUnschedule,
} from './createScheduleSubMenu.locstring.json';
import loc from 'owa-localize';
import { logUsage } from 'owa-analytics';
import formatUserTime from 'owa-datetime/lib/formatters/formatUserTime';
import formatWeekDayTime from 'owa-datetime/lib/formatters/formatWeekDayTime';
import addDays from 'owa-datetime/lib/owaDate/addDays';
import addHours from 'owa-datetime/lib/owaDate/addHours';
import isAfter from 'owa-datetime/lib/owaDate/isAfter';
import isSameDay from 'owa-datetime/lib/owaDate/isSameDay';
import { OwaDate, owaDate } from 'owa-datetime/lib/owaDate/owaDate';
import startOfDay from 'owa-datetime/lib/owaDate/startOfDay';
import startOfHour from 'owa-datetime/lib/owaDate/startOfHour';
import now from 'owa-datetime/lib/userDate/now';
import { isFeatureEnabled } from 'owa-feature-flags';
import showScheduleDatePickerDialog from 'owa-mail-datepicker/lib/components/ScheduleDatePicker';
import { listViewStore, TableQueryType, TableView } from 'owa-mail-list-store';
import DayOfWeek from 'owa-service/lib/contract/DayOfWeek';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';

import {
    IContextualMenuItem,
    IContextualMenuProps,
    ContextualMenuItemType,
} from 'office-ui-fabric-react/lib/ContextualMenu';

// $NOTE(fpintos): There is bias in these hard-coded hours.
// For example, I never schedule anything at 8am because I sleep late.
// Perhaps we should be considering the working hours in user options for this.
const hourOffsetDP = 1;
const hourOffsetRegular = 8;
const hourOffsetLater = 3;
const hourOffsetEvening = 18;
const hourOffsetWeekend = 10;

const styles = require('../components/ScheduleDatePicker.scss');

// Minimum schedule time for DateTimePicker
export function datePickerMinTime(date: OwaDate) {
    return addHours(startOfHour(date), hourOffsetDP);
}

// Schedule 3 hours into future
export function laterDate(date: OwaDate) {
    return addHours(startOfHour(date), hourOffsetLater);
}

// Schedule for evening
function eveningDate(date: OwaDate) {
    return addHours(startOfDay(date), hourOffsetEvening);
}

// Schedule for tomorrow
function tomorrowDate(date: OwaDate) {
    return addHours(addDays(startOfDay(date), 1), hourOffsetRegular);
}

// Schedule for weekend (next saturday)
function weekendDate(date: OwaDate) {
    // $NOTE(fpintos): UserOptions might say that Saturday is considered a weekday.
    // Do we want to take that into consideration here?
    return getNextDay(date, DayOfWeek.Saturday, hourOffsetWeekend);
}

// Schedule for next week (next monday)
function firstWeekdayDate(date: OwaDate) {
    // $NOTE(fpintos): UserOptions might say that Monday is not considered a weekday.
    // Do we want to take that into consideration here?
    let nextWeekday = getNextDay(date, DayOfWeek.Monday, hourOffsetRegular);

    // This avoids the condition where we schedule on monday and wrap around to
    // the same monday (today), so we must add 7 to get next week's start.
    if (isAfter(date, nextWeekday)) {
        nextWeekday = addDays(nextWeekday, 7);
    }
    return nextWeekday;
}

// Calculate the next day that satisfies offset
function getNextDay(date: OwaDate, dow: number, hour: number) {
    return owaDate(
        date.tz,
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + ((dow + (7 - date.getDay())) % 7),
        hour
    );
}

// Schedule for 8 AM
export function morningDate(date: OwaDate) {
    return addHours(startOfDay(date), hourOffsetRegular);
}

// Log abandonment scenario
function onDismiss() {
    logUsage('TnS_ScheduleItemDismiss', null, { isCore: true });
}

export default function createScheduleSubMenu(
    tableViewId: string,
    onScheduleItemClicked: (tableView: TableView, date: OwaDate) => void
): IContextualMenuProps {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const scheduleSubMenu: IContextualMenuItem[] = [];
    const currentTime = now();
    const hourLater = datePickerMinTime(currentTime);
    const laterToday = laterDate(currentTime);
    const tomorrow = tomorrowDate(currentTime);
    const evening = eveningDate(currentTime);
    const weekend = weekendDate(currentTime);
    const firstWeekday = firstWeekdayDate(currentTime);

    scheduleSubMenu.push({
        key: loc(scheduleLaterToday),
        name: loc(scheduleLaterToday),
        iconProps: {
            iconName: 'Clock',
        },
        secondaryText: formatUserTime(laterToday),
        onClick: () => {
            onScheduleItemClicked(tableView, laterToday);
        },
    });

    // If laterToday does not surpass evening time stamp AND laterToday does not
    // spill over into the next day, show laterToday.
    if (isAfter(evening, laterToday) && isSameDay(laterToday, currentTime)) {
        scheduleSubMenu.push({
            key: loc(scheduleThisEvening),
            name: loc(scheduleThisEvening),
            iconProps: {
                iconName: 'ClearNight',
            },
            secondaryText: formatUserTime(evening),
            onClick: () => {
                onScheduleItemClicked(tableView, evening);
            },
        });
    }

    scheduleSubMenu.push({
        key: loc(scheduleTomorrow),
        name: loc(scheduleTomorrow),
        iconProps: {
            iconName: 'CoffeeScript',
        },
        secondaryText: formatWeekDayTime(tomorrow),
        onClick: () => {
            onScheduleItemClicked(tableView, tomorrow);
        },
    });

    scheduleSubMenu.push({
        key: loc(scheduleThisWeekend),
        name: loc(scheduleThisWeekend),
        iconProps: {
            iconName: 'Vacation',
        },
        secondaryText: formatWeekDayTime(weekend),
        onClick: () => {
            onScheduleItemClicked(tableView, weekend);
        },
    });

    scheduleSubMenu.push({
        key: loc(scheduleNextWeek),
        name: loc(scheduleNextWeek),
        iconProps: {
            iconName: 'Suitcase',
        },
        secondaryText: formatWeekDayTime(firstWeekday),
        onClick: () => {
            onScheduleItemClicked(tableView, firstWeekday);
        },
    });

    scheduleSubMenu.push({
        key: loc(scheduleChooseDate),
        name: loc(scheduleChooseDate),
        iconProps: {
            iconName: 'Calendar',
        },
        onClick: () => {
            showScheduleDatePickerDialog(
                hourLater,
                date => onScheduleItemClicked(tableView, date),
                tableView
            );
        },
    });

    if (
        tableView.tableQuery.type == TableQueryType.Folder &&
        folderIdToName(tableView.tableQuery.folderId) == 'scheduled'
    ) {
        const keyName = isFeatureEnabled('tri-snooze-exp')
            ? loc(unsnooze)
            : loc(scheduleUnschedule);

        scheduleSubMenu.push({
            key: '-',
            itemType: ContextualMenuItemType.Divider,
        });
        scheduleSubMenu.push({
            key: keyName,
            name: keyName,
            className: styles.unscheduleText,
            iconProps: {
                iconName: 'Blocked',
            },
            onClick: () => {
                onScheduleItemClicked(tableView, undefined /* shouldUnschedule */);
            },
        });
    }

    return {
        items: scheduleSubMenu,
        className: styles.fixWidthContextMenu,
        directionalHintFixed: true,
        onMenuDismissed: () => onDismiss(),
    };
}
